import React, { useEffect } from "react";
import { NotificationAlertContext } from "../../contexts/notificationAlertContext.js";
import { useHistory, useParams } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import {
  getArchivedMessageManifest,
  invokeMCResendMessagesTask,
} from "../../graphql/queries.js";
// react-bootstrap components
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  OverlayTrigger,
  Tooltip,
  Button,
  Spinner,
} from "react-bootstrap";

import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Chat from "views/Components/Chat.js";
import { tourGuideSteps } from "views/Components/ViewManifestSteps.js";
import { AuthContext } from "contexts/authContext.js";
import { useTour } from "@reactour/tour";
import { convertDateToLocaleString } from "utilities/dateUtilities.js";
import { copyToClipboard } from "utilities/commonUtilities.js";

const ViewMessage = (props) => {
  // Contexts
  const notify = React.useContext(NotificationAlertContext);
  const currentUser = React.useContext(AuthContext);
  const history = useHistory();

  // Get manifestId
  let { manifestId } = useParams();
  manifestId = manifestId.split("?")[0];

  const searchParams = new URLSearchParams(window.location.search);

  const clientId = searchParams.get("clientId");
  const clientEnvironmentId = searchParams.get("clientEnvironmentId");
  const channelId = searchParams.get("channelId");
  const messageId = searchParams.get("messageId");
  const dateRollup = searchParams.get("dateRollup");

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  // SSO related information
  const clientName = props.location.state?.clientName;
  const channelName = props.location.state?.channelName;
  const clientEnvironmentName = props.location.state?.clientEnvironmentName;

  const [manifestDetails, setManifestDetails] = React.useState({
    environment_id: "",
    channel_id: "",
    manifest_id: "",
    msg_id: 0,
    sent_total: 0,
    error_total: 0,
    filter_total: 0,
    raw_message_bytes: 0.0,
    date_received: "",
    date_completed: "",
    date_rollup: "",
    archive_bundle_path: "",
    message_bundle_details: null,
    flat_message_details: null,
    raw_data: {},
  });

  const [connectorData, setConnectorData] = React.useState([]);
  const [showLoader, setShowLoader] = React.useState(false);
  const [viewChatModal, setViewChatModal] = React.useState(false);
  const [initialPrompt, setInitialPrompt] = React.useState("");

  const [alert, setAlert] = React.useState(null);
  
  // Edit states
  const [isMessageEditable, setIsMessageEditable] = React.useState(false);
  const [isAttachmentEditable, setIsAttachmentEditable] = React.useState(false);
  const [isMetadataEditable, setIsMetadataEditable] = React.useState(false);
  const [editedMessage, setEditedMessage] = React.useState("");
  const [editedAttachment, setEditedAttachment] = React.useState("");
  const [editedMetadata, setEditedMetadata] = React.useState("");

  const descriptionStyle = {
    minWidth: "100%",
    minHeight: "200px",
  };
  const zindex = {
    zIndex: "9999",
  };
  const ellipsisStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "300px",
  };
  const noMargin = {
    margin: 0,
  };

  const fetchArchivedMessageManifest = async (param) => {
    try {
      setShowLoader(true);
      const response = await API.graphql(
        graphqlOperation(getArchivedMessageManifest, param)
      );
      if (response.data?.getArchivedMessageManifest?.success) {
        const message = response.data.getArchivedMessageManifest.body;
        setManifestDetails({...message, message_bundle_details: message.message_bundle_details ? {
          attachment_path: message.message_bundle_details.attachment_path,
          message_path: message.message_bundle_details.message_path,
          metadata_path: message.message_bundle_details.metadata_path
        } : null,
        flat_message_details: message.flat_message_details ? {
          attachment_path: message.flat_message_details.attachment_path,
          message_path: message.flat_message_details.message_path,
          metadata_path: message.flat_message_details.metadata_path
        } : null,});
        setConnectorData(message.connector_details);

        notify("success");
      } else {
        console.error(response.data?.getArchivedMessageManifest?.message);
        notify("danger");
        history.goBack();
      }
      return response.data?.getArchivedMessageManifest?.success;
    } catch (error) {
      console.error(error);
      notify("danger");
      history.goBack();
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      setShowLoader(true);
      const parameters = {
        clientEnvironmentId: clientEnvironmentId,
        channelId: channelId,
        messageId: messageId,
        manifestId: manifestId,
        dateRollup: dateRollup,
      };
      const isSuccess = await fetchArchivedMessageManifest(parameters);
      if (!isSuccess) {
        notify("danger");
        history.goBack();
      }
    }
    fetchData();
  }, []);

  const reprocessMessage = async () => {
    try {
      setShowLoader(true);

      // Check if any content has been edited
      const hasMessageEdit = isMessageEditable || (editedMessage !== "" && editedMessage !== manifestDetails.raw_data.message);
      const hasAttachmentEdit = isAttachmentEditable || (editedAttachment !== "" && editedAttachment !== manifestDetails.raw_data.attachment);
      const hasMetadataEdit = isMetadataEditable || (editedMetadata !== "" && editedMetadata !== manifestDetails.raw_data.metadata);
      const hasAnyEdit = hasMessageEdit || hasAttachmentEdit || hasMetadataEdit;

      if (hasMetadataEdit) {
        try {
          JSON.parse(editedMetadata);
        } catch (error) {
          notify("danger", "Invalid JSON format in Metadata.");
          return;
        }
      }

      let task_data;
      let task_object_data = [];

      if (hasAnyEdit) {
        // Generate UUIDs for edited content
        const newUUID = uuidv4();

        // Create edited messages payload
        let editedMessageContent = {
          date_rollup: manifestDetails.date_rollup,
          manifest_id: manifestDetails.manifest_id,
          msg_id: manifestDetails.msg_id
        };

        if (manifestDetails.raw_data.message !== undefined && manifestDetails.raw_data.message !== null) {
          editedMessageContent.message_object_name = `${newUUID}.raw`;
        }

        if (manifestDetails.raw_data.metadata !== undefined && manifestDetails.raw_data.metadata !== null) {
          editedMessageContent.metadata_object_name = `${newUUID}.metadata`;
        }

        if (manifestDetails.raw_data.attachment !== undefined && manifestDetails.raw_data.attachment !== null) {
          editedMessageContent.attachment_object_name = `${newUUID}.attachment`;
        }

        task_data = {
          channel_id: channelId,
          edited_messages: [editedMessageContent]
        };

        // Create task object data for edited content
        if (manifestDetails.raw_data.message !== undefined && manifestDetails.raw_data.message !== null) {
          task_object_data.push({
            object_content: hasMessageEdit ? editedMessage : manifestDetails.raw_data.message,
            object_name: `${newUUID}.raw`
          });
        }
        
        if (manifestDetails.raw_data.metadata !== undefined && manifestDetails.raw_data.metadata !== null) {
          task_object_data.push({
            object_content: hasMetadataEdit ? editedMetadata : manifestDetails.raw_data.metadata,
            object_name: `${newUUID}.metadata`
          });
        }
        
        if (manifestDetails.raw_data.attachment !== undefined && manifestDetails.raw_data.attachment !== null) {
          task_object_data.push({
            object_content: hasAttachmentEdit ? editedAttachment : manifestDetails.raw_data.attachment,
            object_name: `${newUUID}.attachment`
          });
        }
      } else {
        // Original payload structure for non-edited messages
        task_data = {
          messages: [
            {
              msg_id: manifestDetails.msg_id,
              is_bundle: manifestDetails.is_bundle,
              date_rollup: manifestDetails.date_rollup,
              manifest_id: manifestDetails.manifest_id,
              archive_bundle_path: manifestDetails.archive_bundle_path,
              flat_message_details: manifestDetails.flat_message_details,
              message_bundle_details: manifestDetails.message_bundle_details,
            }
          ],
          channel_id: channelId,
        };
      }

      // Create the invokeMCResendMessagesTask payload
      const taskPayload = {
        client_id: clientId,
        client_environment_id: clientEnvironmentId,
        task_data: JSON.stringify(task_data),
        task_object_data: JSON.stringify(task_object_data),
      };

      // Check payload size (5 MB limit = 5 * 1024 * 1024 bytes)
      const payloadSize = new Blob([JSON.stringify(taskPayload)]).size;
      const MAX_PAYLOAD_SIZE = 5 * 1024 * 1024;
      if (payloadSize > MAX_PAYLOAD_SIZE) {
        notify("danger", "Payload size exceeds 5 MB. Please reduce the size of the edited sections.");
        return;
      }

      notify("info");

      const response = await API.graphql(
        graphqlOperation(invokeMCResendMessagesTask, taskPayload)
      );

      if (response.data?.invokeMCResendMessagesTask?.success) {
        notify(
          "success",
          `Reprocess task created successfully. Task ID: ${response.data.invokeMCResendMessagesTask.body?.task_id}`
        );
      } else {
        console.error(response.data?.invokeMCResendMessagesTask?.message);
        notify("danger", "Failed to create reprocess task");
      }
    } catch (error) {
      console.error(error);
      notify("danger", "An error occurred while creating the reprocess task");
    } finally {
      setShowLoader(false);
    }
  };

  if (showLoader) return <Spinner animation="grow" />;
  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Message Details</h2>
          </Col>
          <Col className="my-auto text-right">
            {currentUser.groupName === "external" && (
              <Button
                variant="secondary"
                className="btn-outline"
                style={{ marginRight: "10px" }}
                onClick={() => {
                  // Filter out steps for conditionally rendered elements
                  let filteredSteps = tourGuideSteps.filter(step => {
                    if (step.selector === "#message-details" && 
                        (manifestDetails.raw_data.message === undefined || manifestDetails.raw_data.message === null)) {
                      return false;
                    }
                    if (step.selector === "#attachment-details" && 
                        (manifestDetails.raw_data.attachment === undefined || manifestDetails.raw_data.attachment === null)) {
                      return false;
                    }
                    if (step.selector === "#metadata-details" && 
                        (manifestDetails.raw_data.metadata === undefined || manifestDetails.raw_data.metadata === null)) {
                      return false;
                    }
                    if (step.selector === "#reprocess-message-button" && 
                        (manifestDetails.raw_data.message === undefined || manifestDetails.raw_data.message === null)) {
                      return false;
                    }
                    return true;
                  });
                  setSteps(filteredSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            )}
            <Button
              id="reprocess-message-button"
              className="btn-outline"
              variant="primary"
              onClick={() => {
                reprocessMessage();
              }}
            >
              <i className="fas fa-redo-alt"></i> Reprocess Message
            </Button>
          </Col>
        </Row>
        <Card id="client-details-card">
          <Card.Body>
            <Row>
              <Col md="12">
                <h3>Client Details</h3>
                <p style={noMargin}>Client Name: {clientName}</p>
                <p style={noMargin}>
                  Environment Name: {clientEnvironmentName}
                </p>
                <p style={noMargin}>Channel Name: {channelName}</p>
                <p style={noMargin}>
                  Message Received Date:{" "}
                  {convertDateToLocaleString(manifestDetails.date_received)}
                </p>
                <p style={noMargin}>
                  Message Completed Date:{" "}
                  {convertDateToLocaleString(manifestDetails.date_completed)}
                </p>
                <p style={noMargin}>Message Id: {manifestDetails.msg_id}</p>
                <p style={noMargin}>
                  Size In Bytes: {manifestDetails.raw_message_bytes}
                </p>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <Row>
              <Col md="12" id="connector-details">
                <h3>Connector Details</h3>
                {connectorData && connectorData.length > 0 && (
                  <>
                    <Table className="table-hover table-striped w-full">
                      <thead>
                        <tr>
                          <th>Connector ID</th>
                          <th>Connector Name</th>
                          <th>Connector Message Status</th>
                          <th>Connector Error Text</th>
                          <th></th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {connectorData.map((connector, key) => {
                          return (
                            <tr key={key}>
                              <td>{connector.connector_id}</td>
                              <td>{connector.connector_name}</td>
                              <td>{connector.connector_status}</td>
                              <td>
                                {
                                  <OverlayTrigger
                                    key={`event-type-${connector.connector_id}`}
                                    placement="right"
                                    overlay={
                                      <Tooltip
                                        id={`event-type-tooltip-${connector.connector_id}`}
                                        style={zindex}
                                      >
                                        {connector.connector_error_text}
                                      </Tooltip>
                                    }
                                  >
                                    <div style={ellipsisStyle}>
                                      {connector.connector_error_text
                                        ? connector.connector_error_text
                                        : ""}
                                    </div>
                                  </OverlayTrigger>
                                }
                              </td>
                              <td>
                                {connector.connector_status === "ERROR" && (
                                  <OverlayTrigger
                                    key={`copy-error-${connector.connector_id}`}
                                    placement="right"
                                    overlay={
                                      <Tooltip
                                        id={`copy-error-tooltip-${connector.connector_id}`}
                                      >
                                        Copy connector error text
                                      </Tooltip>
                                    }
                                  >
                                    <Button
                                      variant="primary"
                                      className="btn-outline"
                                      onClick={() => {
                                        copyToClipboard(connector.connector_error_text);
                                      }}
                                    >
                                      <i className="fas fa-copy"></i>
                                    </Button>
                                  </OverlayTrigger>
                                )}
                              </td>
                              <td>
                                {connector.connector_status === "ERROR" && (
                                  <Button
                                    variant="primary"
                                    className="btn-outline"
                                    onClick={() => {
                                      setInitialPrompt(
                                        `Can you help me fix this Mirth Connect channel error?\n${connector.connector_error_text}`
                                      );
                                      setViewChatModal(true);
                                    }}
                                  >
                                    Mirth Assist
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {manifestDetails.raw_data.message !== undefined && manifestDetails.raw_data.message !== null && (
        <Card>
          <Card.Body>
            <h3>Message</h3>
            <Row>
              <Col md="12" id="message-details">
                <textarea
                  id="messageDescription"
                  style={descriptionStyle}
                  disabled={!isMessageEditable}
                  className="form-control"
                  value={isMessageEditable ? editedMessage : manifestDetails.raw_data.message}
                  onChange={(event) => setEditedMessage(event.target.value)}
                ></textarea>
              </Col>
            </Row>
            <Row>
              <Col>
                <div style={{ float: "right", marginTop: "10px" }}>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    disabled={!isMessageEditable}
                    style={{ marginRight: "10px" }}
                    onClick={() => {
                      setEditedMessage(manifestDetails.raw_data.message);
                      setIsMessageEditable(false);
                    }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    disabled={isMessageEditable}
                    onClick={() => {
                      setEditedMessage(manifestDetails.raw_data.message);
                      setIsMessageEditable(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        )}
        {manifestDetails.raw_data.attachment !== undefined && manifestDetails.raw_data.attachment !== null && (
        <Card>
          <Card.Body>
            <h3>Attachment</h3>
            <Row>
              <Col md="12" id="attachment-details">
                <textarea
                  id="attachmentDescription"
                  style={descriptionStyle}
                  disabled={!isAttachmentEditable}
                  className="form-control"
                  value={isAttachmentEditable ? editedAttachment : manifestDetails.raw_data.attachment}
                  onChange={(event) => setEditedAttachment(event.target.value)}
                ></textarea>
              </Col>
            </Row>
            <Row>
              <Col>
                <div style={{ float: "right", marginTop: "10px" }}>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    disabled={!isAttachmentEditable}
                    style={{ marginRight: "10px" }}
                    onClick={() => {
                      setEditedAttachment(manifestDetails.raw_data.attachment);
                      setIsAttachmentEditable(false);
                    }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    disabled={isAttachmentEditable}
                    onClick={() => {
                      setEditedAttachment(manifestDetails.raw_data.attachment);
                      setIsAttachmentEditable(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        )}
        {manifestDetails.raw_data.metadata !== undefined && manifestDetails.raw_data.metadata !== null && (
        <Card>
          <Card.Body>
            <h3>Metadata</h3>
            <Row>
              <Col md="12" id="metadata-details">
                <textarea
                  id="metadataDescription"
                  style={descriptionStyle}
                  disabled={!isMetadataEditable}
                  className="form-control"
                  value={isMetadataEditable ? editedMetadata : manifestDetails.raw_data.metadata}
                  onChange={(event) => setEditedMetadata(event.target.value)}
                ></textarea>
              </Col>
            </Row>
            {/* <Row>
              <Col>
                <div style={{ float: "right", marginTop: "10px" }}>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    disabled={!isMetadataEditable}
                    style={{ marginRight: "10px" }}
                    onClick={() => {
                      setEditedMetadata(manifestDetails.raw_data.metadata);
                      setIsMetadataEditable(false);
                    }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    className="btn-simple"
                    disabled={isMetadataEditable}
                    onClick={() => {
                      setEditedMetadata(manifestDetails.raw_data.metadata);
                      setIsMetadataEditable(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </Col>
            </Row> */}
          </Card.Body>
        </Card>
        )}
        <Modal
          toggle={() => setViewChatModal(!viewChatModal)}
          size="xl"
          isOpen={viewChatModal}
          scrollable={true}
        >
          <ModalHeader style={{ justifyContent: "center" }}>
            Mirth Assist
          </ModalHeader>
          <ModalBody>
            <Chat
              clientId={clientId}
              clientEnvironmentId={clientEnvironmentId}
              channelId={channelId}
              inboundMessageIdempotencyKey={null}
              initialPrompt={initialPrompt}
            />
          </ModalBody>
          <ModalFooter style={{ justifyContent: "center" }}>
            <Button
              variant="secondary"
              style={{ marginRight: "10px" }}
              onClick={() => {
                setViewChatModal(false);
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
};

export default ViewMessage;
